import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import '../styles/styles.scss';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Lazy load ALL pages including HomePage
const HomePage = lazy(() => import('./home-component/home-page'));
const BannerThankYouPage = lazy(() => import('./banner-thank-you-component/thank-you-page'));
const BrochureThankYouPage = lazy(() => import('./brochure-thank-you-component/thank-you-page'));

const App = () => {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true, easing: 'ease-in-out' });

    // Lenis smooth scroll, driven by GSAP's ticker so it stays in
    // perfect sync with ScrollTrigger-based animations.
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    // Lenis owns the scroll position every animation frame — a plain
    // `window.scrollTo(...)` call from elsewhere (e.g. the Interiors
    // section's Prev/Next buttons) gets silently fought/overridden on the
    // very next frame. Exposing the instance lets other components call
    // `window.lenis.scrollTo(...)` instead, which Lenis actually respects.
    window.lenis = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    const update = (time) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(update);
    gsap.ticker.lagSmoothing(0);

    // As lazy-loaded sections mount below the fold and inject their own
    // pinned spacers, ScrollTrigger recalculates on "refresh" — but Lenis
    // caches its own scroll-height limit and won't know the page grew
    // unless told to. Without this, later sections (e.g. a pinned gallery
    // near the bottom) can end up with a pin range Lenis thinks is beyond
    // the scrollable area, which is what makes pins silently stop working.
    const handleRefresh = () => lenis.resize();
    ScrollTrigger.addEventListener('refresh', handleRefresh);
    ScrollTrigger.refresh();

    return () => {
      gsap.ticker.remove(update);
      ScrollTrigger.removeEventListener('refresh', handleRefresh);
      lenis.destroy();
      if (window.lenis === lenis) {
        window.lenis = null;
      }
    };
  }, []);

  return (
    <Routes>
      <Route path="/">
        <Route index element={
          <Suspense fallback={<div />}>
            <HomePage />
          </Suspense>
        } />
        <Route path="/home" element={
          <Suspense fallback={<div />}>
            <HomePage />
          </Suspense>
        } />
        <Route path="/banner-thank-you" element={
          <Suspense fallback={<div />}>
            <BannerThankYouPage />
          </Suspense>
        } />
        <Route path="/brochure-thank-you" element={
          <Suspense fallback={<div />}>
            <BrochureThankYouPage />
          </Suspense>
        } />
      </Route>
    </Routes>
  );
};

export default App;